import React, {
  Context,
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react";
import { ILayoutProps } from "models/base/ILayoutProps";
import SeoContextProvider, { ISeoContext } from "contexts/SeoContext";
import SeoHeadWidget from "componentLayout/SEO/SeoHeadWidget";
import RoutesObjectContextProvider from "contexts/RoutesObjectContext";
import AssetsContextProvider, { IAssetsContext } from "contexts/AssetsContext";
import { useGtmDefaultData } from "GTMs/GtmData";
import { isBrowser } from "utils/nookies/utils";
import Alert from "@mui/material/Alert";
import useHandleWebView from "../hooks/useHandleWebView";


interface IAppContext extends ILayoutProps, ISeoContext, IAssetsContext {
  supportBottomNavigation?: boolean;
  setSupportBottomNavigation?: (boolean) => void;
  isWebView?: boolean;
  isDarkMode?: boolean;
}

// Create Context
const AppContext: Context<IAppContext> = createContext({
  supportBottomNavigation: undefined,
  setSupportBottomNavigation: undefined,
  seoPage: undefined,
  setSeoPage: undefined,
  categories: undefined,
  subCategories: undefined,
  cities: undefined,
  countries: undefined,
  neighborhoodsAssets: undefined,
  virtualCategories: undefined,
  dynamicAttributesEnhanced: undefined,
  isWebView: undefined,
  isDarkMode: undefined,
});

export function useAppContext(): IAppContext {
  return useContext(AppContext);
}

const AppContextProvider: FC<IAppContext> = ({ children, ...pageProps }) => {
  const [supportBottomNavigation, setSupportBottomNavigation] =
    useState<boolean>();
  const { isWebView, isDarkMode } = useHandleWebView();
  useGtmDefaultData();
  const [error, setError] = useState("");
  useEffect(() => {
    const assetsFailedToLoadCb = (e) => {
      setError(e.detail.message);
    };
    window.addEventListener("assetsFailedToLoad", assetsFailedToLoadCb);
    return () =>
      window.removeEventListener("assetsFailedToLoad", assetsFailedToLoadCb);
  }, []);

  useEffect(() => {
    return () => setSupportBottomNavigation(undefined);
  }, [isBrowser() ? window.location.href : null]);

  return (
    <AppContext.Provider
      value={{
        ...pageProps,
        supportBottomNavigation,
        setSupportBottomNavigation,
        isWebView,
        isDarkMode,
      }}
    >
      {error && (
        <Alert className="redColor" severity={"error"}>
          {error}
        </Alert>
      )}
      <RoutesObjectContextProvider {...pageProps}>
        <SeoContextProvider {...pageProps}>
          <AssetsContextProvider {...pageProps}>
            <SeoHeadWidget />
                {children}
          </AssetsContextProvider>
        </SeoContextProvider>
      </RoutesObjectContextProvider>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
